/**
 * Product Selection Manager Class
 * Handles the product selection functionality for Buy 1-4 cards
 * Manages selected products array and card updates
 */
class ProductSelectionManager {
  constructor() {
    this.selectedProducts = [];
    this.maxProducts = 4;
    this.replacementCardIndex = null;
    this._skipCommitOnNextVariantsClose = false;
    this._isCommittingSelections = false;
    this._isSyncingFromCart = false;
    this._queuedCartSync = false;
    this._cartSyncTimer = null;
    this._cartDrawerObserver = null;
    this._selectionToCartSyncTimer = null;
    this._isSyncingSelectionToCart = false;
    this._queuedSelectionToCartSync = false;
    this._selectionSyncVersion = 0;
    this._pendingFirstAddSignature = '';
    this._pendingFirstAddPromise = null;
    this._suspendSelectionToCartUntil = 0;
    this._isApplyingOptimisticFromCart = false;
    this._deferCartHydrationUntil = 0;
    this._deferCartHydrationTimer = null;
    this._committedVariantQuantities = new Map();
    this.init();
  }



  /**
   * Initialize the product selection manager
   */
  init() {
    this.maxProducts = this.getBaseSlotCount();
    this.bindEvents();
    this.updateCards();
    this.updatePricingDisplay();
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    this.bindCardEvents();
    this.bindModalEvents();
    this.bindAddToCartEvents();
    this.bindQuantityEvents();
    this.bindSubscriptionEvents();
    this.bindCartSyncEvents();
  }

  bindCartSyncEvents() {
    if (this.isVariantGridAtcActive()) return;
    const bindImmediateDrawerClick = () => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || drawer.dataset.psmImmediateBound === 'true') return;
      drawer.dataset.psmImmediateBound = 'true';
      drawer.addEventListener(
        'click',
        (event) => {
          const drawerMutationTrigger = event.target?.closest?.(
            '[data-cs-qty-action], [data-cs-remove-group], .cart-remove-button, [data-psm-qty-action], [data-psm-remove-group]'
          );
          if (!drawerMutationTrigger) return;
          event._psmHandled = true;
          this.applyOptimisticSelectionFromCartDrawerInteraction(drawerMutationTrigger);
        },
        true
      );
    };

    document.addEventListener('cart:updated', () => {
      bindImmediateDrawerClick();
    });

    // Cart drawer qty/remove actions should reflect instantly in selection UI.
    document.addEventListener('click', (event) => {
      if (event._psmHandled) return;
      const drawerMutationTrigger = event.target.closest(
        'cart-drawer [data-cs-qty-action], cart-drawer [data-cs-remove-group], #CartDrawer .cart-remove-button, cart-drawer [data-psm-qty-action], cart-drawer [data-psm-remove-group]'
      );
      if (!drawerMutationTrigger) return;
      this.applyOptimisticSelectionFromCartDrawerInteraction(drawerMutationTrigger);
    });

    bindImmediateDrawerClick();

    // Hydrate UI from the actual cart on first load.
    this.queueSyncSelectionStateFromCart();
  }

  getBaseSlotCount() {
    const originalSlides = document.querySelectorAll('.product-selection-swiper .swiper-slide:not(.duplicate-slide)');
    return Math.max(1, originalSlides.length || this.maxProducts || 4);
  }

  isVariantGridAtcActive() {
    return Boolean(
      document.querySelector('variant-selects[data-cs-variant-grid-atc="true"] .cs-variant-grid-atc[data-variant-id]')
    );
  }

  scheduleCartSyncFromDrawerMutation(delay = 140) {
    if (this.isVariantGridAtcActive()) return;
    if (this._cartSyncTimer) window.clearTimeout(this._cartSyncTimer);
    this._cartSyncTimer = window.setTimeout(() => {
      this._cartSyncTimer = null;
      this.queueSyncSelectionStateFromCart();
    }, delay);
  }

  beginOptimisticHydrationDefer(durationMs = 900) {
    if (this.isVariantGridAtcActive()) return;
    const until = Date.now() + durationMs;
    this._deferCartHydrationUntil = Math.max(this._deferCartHydrationUntil || 0, until);
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.dataset.psmSuppressRefreshUntil = String(this._deferCartHydrationUntil);
    }
    if (this._deferCartHydrationTimer) window.clearTimeout(this._deferCartHydrationTimer);
    this._deferCartHydrationTimer = window.setTimeout(() => {
      this._deferCartHydrationTimer = null;
      if (cartDrawer) {
        const current = parseInt(cartDrawer.dataset.psmSuppressRefreshUntil || '0', 10) || 0;
        if (Date.now() >= current) {
          delete cartDrawer.dataset.psmSuppressRefreshUntil;
        }
      }
      this.applyOptimisticCartDrawerFromSelection();
    }, durationMs + 20);
  }

  applyOptimisticSelectionFromCartDrawerInteraction(triggerEl) {
    if (this.isVariantGridAtcActive()) return;
    const item = triggerEl.closest('.cs-cart-item[data-cs-variant-id]');
    if (!item) return;

    const variantId = String(item.dataset.csVariantId || '').trim();
    if (!variantId) return;

    const selectedQtyByVariant = this.getSelectedVariantQuantities();
    const rowQty = Math.max(0, parseInt(item.querySelector('.cs-qty__count')?.textContent || '0', 10) || 0);
    const currentQty = Math.max(0, rowQty || (parseInt(selectedQtyByVariant.get(variantId) || 0, 10) || 0));
    let nextQty = currentQty;

    if (triggerEl.matches('[data-cs-remove-group], [data-psm-remove-group], .cart-remove-button')) {
      nextQty = 0;
    } else if (triggerEl.matches('[data-cs-qty-action="plus"], [data-psm-qty-action="plus"]')) {
      nextQty = currentQty + 1;
    } else if (triggerEl.matches('[data-cs-qty-action="minus"], [data-psm-qty-action="minus"]')) {
      nextQty = Math.max(0, currentQty - 1);
    } else {
      return;
    }

    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
    if (!variantCard) return;
    const variantData = this.getVariantDataFromCard(variantCard);
    if (!variantData) return;

    this._isApplyingOptimisticFromCart = true;
    try {
      this._suspendSelectionToCartUntil = Date.now() + 1200;
      if (this._selectionToCartSyncTimer) {
        window.clearTimeout(this._selectionToCartSyncTimer);
        this._selectionToCartSyncTimer = null;
      }
      this.beginOptimisticHydrationDefer(1200);
      this.updateSelectedProductsByQuantity(variantData, currentQty, nextQty);
      this.syncQuantities(variantId, nextQty);
      this.updateCards();
    } finally {
      this._isApplyingOptimisticFromCart = false;
    }
  }

  setupCartDrawerObserver() {
    if (this._cartDrawerObserver) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;

    this._cartDrawerObserver = new MutationObserver(() => {
      this.scheduleCartSyncFromDrawerMutation(180);
    });
    this._cartDrawerObserver.observe(drawer, {
      childList: true,
      subtree: true,
    });
  }

  queueSyncSelectionStateFromCart() {
    if (this.isVariantGridAtcActive()) return;
    const cartDrawer = document.querySelector('cart-drawer');
    if (this._pendingFirstAddPromise) {
      this._queuedCartSync = true;
      this.scheduleCartSyncFromDrawerMutation(180);
      return;
    }
    const hasPendingDrawerMutations =
      cartDrawer &&
      ((typeof cartDrawer.hasPendingCartMutations === 'function' && cartDrawer.hasPendingCartMutations()) ||
        (typeof cartDrawer.hasPendingGroupedWork === 'function' && cartDrawer.hasPendingGroupedWork()) ||
        (cartDrawer._pendingMutations && cartDrawer._pendingMutations.size));
    if (hasPendingDrawerMutations) {
      this.scheduleCartSyncFromDrawerMutation(220);
      return;
    }

    if (Date.now() < (this._deferCartHydrationUntil || 0)) {
      this._queuedCartSync = true;
      return;
    }
    if (this._isSyncingSelectionToCart || this._isCommittingSelections) {
      this._queuedCartSync = true;
      return;
    }
    const variantsModal = document.getElementById('productVariantsModal');
    if (variantsModal && variantsModal.open) {
      this._queuedCartSync = true;
      return;
    }
    if (this._isSyncingFromCart) {
      this._queuedCartSync = true;
      return;
    }
    this.syncSelectionStateFromCart();
  }

  async syncSelectionStateFromCart() {
    if (this.isVariantGridAtcActive()) return;
    if (this._isSyncingFromCart) return;

    this._isSyncingFromCart = true;
    try {
      const variantCards = Array.from(document.querySelectorAll('.product-variant-card[data-variant-id]'));
      if (!variantCards.length) return;

      const variantDataById = new Map();
      variantCards.forEach((card) => {
        const id = String(card.dataset.variantId || '').trim();
        if (!id) return;
        variantDataById.set(id, this.getVariantDataFromCard(card));
      });

      const cartQtyByVariantId = new Map();
      const cartDrawer = document.querySelector('cart-drawer');
      const cartItems = Array.from(cartDrawer?.querySelectorAll('.cs-cart-item[data-cs-variant-id]') || []);
      cartItems.forEach((item) => {
        const id = String(item.dataset.csVariantId || '').trim();
        if (!id || !variantDataById.has(id)) return;
        const qtyEl = item.querySelector('.cs-qty__count');
        const qty = Math.max(0, parseInt(qtyEl?.textContent || '0', 10) || 0);
        cartQtyByVariantId.set(id, (cartQtyByVariantId.get(id) || 0) + qty);
      });

      const nextSelectedProducts = [];
      variantDataById.forEach((variantData, id) => {
        const qty = cartQtyByVariantId.get(id) || 0;
        this.syncQuantities(id, qty);
        for (let i = 0; i < qty; i++) {
          nextSelectedProducts.push({
            ...variantData,
            quantity: 1,
            addedAt: new Date().toISOString(),
            instanceIndex: i,
          });
        }
      });

      this.selectedProducts = nextSelectedProducts;
      this._committedVariantQuantities = this.getSelectedVariantQuantities();
      this.syncCartBubbleFromSelection();
      this.updateCards();

      document.dispatchEvent(new CustomEvent('productSelection:changed', {
        detail: { selectedProducts: this.selectedProducts }
      }));
      if (window.customAddToCartSyncManager?.handleProductSelectionChange) {
        window.customAddToCartSyncManager.handleProductSelectionChange();
      }
    } catch (e) {
      // best effort sync only
    } finally {
      this._isSyncingFromCart = false;
      if (this._queuedCartSync) {
        this._queuedCartSync = false;
        this.syncSelectionStateFromCart();
      }
    }
  }

  scheduleSelectionToCartSync(delay = 40) {
    if (this.isVariantGridAtcActive()) return;
    if (this._isSyncingFromCart) return;
    if (Date.now() < (this._suspendSelectionToCartUntil || 0)) {
      const wait = Math.max(20, (this._suspendSelectionToCartUntil - Date.now()) + 20);
      delay = Math.max(delay, wait);
    }
    const cartDrawer = document.querySelector('cart-drawer');
    const hasDrawerRows = Boolean(cartDrawer?.querySelector('.cs-cart-item[data-cs-variant-id]'));
    if (!hasDrawerRows) delay = 0;
    this._selectionSyncVersion += 1;
    const requestedVersion = this._selectionSyncVersion;
    if (this._selectionToCartSyncTimer) window.clearTimeout(this._selectionToCartSyncTimer);
    this._selectionToCartSyncTimer = window.setTimeout(() => {
      this._selectionToCartSyncTimer = null;
      this.syncSelectionStateToCart(requestedVersion);
    }, delay);
  }

  async syncSelectionStateToCart(version = this._selectionSyncVersion) {
    if (this.isVariantGridAtcActive()) return;
    if (this._isSyncingFromCart) return;
    if (Date.now() < (this._suspendSelectionToCartUntil || 0)) {
      this._queuedSelectionToCartSync = true;
      this.scheduleSelectionToCartSync();
      return;
    }
    if (this._isSyncingSelectionToCart) {
      this._queuedSelectionToCartSync = true;
      return;
    }

    this._isSyncingSelectionToCart = true;
    try {
      const variantCards = Array.from(document.querySelectorAll('.product-variant-card[data-variant-id]'));
      if (!variantCards.length) return;

      const managedVariantIds = Array.from(
        new Set(
          variantCards
            .map((card) => String(card.dataset.variantId || '').trim())
            .filter(Boolean)
        )
      );
      if (!managedVariantIds.length) return;

      const targetQtyByVariant = this.getSelectedVariantQuantities();
      const selectedPurchase = this.getCurrentPurchaseSelection();
      const cartDrawer = document.querySelector('cart-drawer');
      const shouldFlushPendingCartDrawerWork =
        cartDrawer &&
        typeof cartDrawer.flushAllPendingNow === 'function' &&
        ((typeof cartDrawer.hasPendingCartMutations === 'function' && cartDrawer.hasPendingCartMutations()) ||
          (typeof cartDrawer.hasPendingGroupedWork === 'function' && cartDrawer.hasPendingGroupedWork()) ||
          (cartDrawer._pendingMutations && cartDrawer._pendingMutations.size));
      if (shouldFlushPendingCartDrawerWork) {
        await cartDrawer.flushAllPendingNow().catch(() => {});
      }

      const cart = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
      if (version !== this._selectionSyncVersion) return;
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];

      const linesByVariant = new Map();
      const desiredPurchaseSignatureSuffix = selectedPurchase.signatureSuffix;
      cartItems.forEach((item) => {
        const variantId = String(item?.variant_id || '').trim();
        if (!variantId || !managedVariantIds.includes(variantId)) return;
        const key = String(item.key || '').trim();
        const qty = parseInt(item.quantity, 10) || 0;
        if (!key) return;
        const itemSellingPlanId = String(item?.selling_plan_allocation?.selling_plan?.id || '').trim();
        const itemPurchaseType =
          itemSellingPlanId || this.isSubscriptionLineItemFromProperties(item?.properties) ? 'subscription' : 'one-time';
        const itemSignature = `${variantId}::${itemSellingPlanId}::${itemPurchaseType}`;

        const grouped = linesByVariant.get(variantId) || { desired: [], other: [] };
        if (itemSignature === `${variantId}::${desiredPurchaseSignatureSuffix}`) {
          grouped.desired.push({ key, quantity: qty });
        } else {
          grouped.other.push({ key, quantity: qty });
        }
        linesByVariant.set(variantId, grouped);
      });

      const updates = {};
      const additions = [];

      managedVariantIds.forEach((variantId) => {
        const desiredQty = Math.max(0, parseInt(targetQtyByVariant.get(variantId) || 0, 10) || 0);
        const grouped = linesByVariant.get(variantId) || { desired: [], other: [] };
        const desiredLines = grouped.desired || [];
        const otherLines = grouped.other || [];

        otherLines.forEach((line) => {
          updates[line.key] = 0;
        });

        let remaining = desiredQty;
        desiredLines.forEach((line) => {
          const next = Math.max(0, Math.min(line.quantity, remaining));
          updates[line.key] = next;
          remaining -= next;
        });

        if (remaining > 0) {
          const item = { id: parseInt(variantId, 10), quantity: remaining };
          if (selectedPurchase.purchaseType === 'subscription' && selectedPurchase.sellingPlanId) {
            item.selling_plan = parseInt(selectedPurchase.sellingPlanId, 10);
          }
          additions.push(item);
        }
      });

      if (Object.keys(updates).length) {
        if (version !== this._selectionSyncVersion) return;
        if (cartDrawer?.updateManyLineItems) {
          await cartDrawer.updateManyLineItems(updates, { withSections: false });
        } else {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
          });
        }
      }

      if (additions.length) {
        if (version !== this._selectionSyncVersion) return;
        if (cartDrawer?.queueMutation && cartDrawer?.requestJson) {
          const hasDrawerRows = Boolean(cartDrawer.querySelector('.cs-cart-item[data-cs-variant-id]:not(.cs-cart-item--optimistic)'));
          const shouldRenderFirstRowFromServer =
            !hasDrawerRows &&
            typeof cartDrawer.applySectionsFromState === 'function' &&
            typeof cartDrawer.getSectionRenderPayload === 'function';
          const payload = shouldRenderFirstRowFromServer
            ? { items: additions, ...cartDrawer.getSectionRenderPayload() }
            : { items: additions };
          const state = await cartDrawer.queueMutation(() =>
            cartDrawer.requestJson('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              cache: 'no-store',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            })
          );
          if (shouldRenderFirstRowFromServer && version === this._selectionSyncVersion) {
            cartDrawer.dataset.psmSkipAutoOpenOnce = 'true';
            cartDrawer.applySectionsFromState(state);
            // Keep first render quantity aligned with current selected state.
            this.applyOptimisticCartDrawerFromSelection();
          }
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: additions }),
          });
        }
      }

      if (version !== this._selectionSyncVersion) return;
      this._committedVariantQuantities = new Map(targetQtyByVariant);
    } catch (e) {
      // best effort sync only
    } finally {
      this._isSyncingSelectionToCart = false;
      if (this._queuedSelectionToCartSync) {
        this._queuedSelectionToCartSync = false;
        this.syncSelectionStateToCart(this._selectionSyncVersion);
      }
    }
  }

  /**
   * Bind card-related event listeners
   */
  bindCardEvents() {
    // Use event delegation for quantity option card clicks to handle both original and duplicate slides
    document.addEventListener('click', (e) => {
      const quantityOption = e.target.closest('.quantity-option');
      if (quantityOption) {
        // Don't trigger if clicking on the clear button
        if (e.target.closest('.quantity-option__clear-btn')) {
          return;
        }
        e.preventDefault();
        
        // For original slides, use the card index + 1
        const slide = quantityOption.closest('.swiper-slide');
        if (slide && !slide.classList.contains('duplicate-slide')) {
          const allOriginalSlides = document.querySelectorAll('.swiper-slide:not(.duplicate-slide)');
          const slideIndex = Array.from(allOriginalSlides).indexOf(slide);
          if (slideIndex !== -1) {
            this.openProductSelection(slideIndex + 1);
          }
        } else if (slide && slide.classList.contains('duplicate-slide')) {
           // For duplicate slides, always open product selection modal
           this.findAndOpenAvailableSlot();
        }
      }
    }); 

    // Note: Add to cart button removed - now using quantity-based selection

    // Override variant card clicks to store selection data
    const variantCards = document.querySelectorAll('.product-variant-card');
    variantCards.forEach(card => {
      // Remove existing event listeners by cloning the element
      const newCard = card.cloneNode(true);
      card.parentNode.replaceChild(newCard, card);
      
      // Add new event listener
      newCard.addEventListener('click', (e) => {
        // Don't trigger variant selection if clicking on quantity controls or add to cart button
        if (e.target.closest('.product-variant-card__quantity') || e.target.closest('.add-to-cart-btn')) {
          return;
        }
        this.selectVariant(newCard);
      });
    });
  }

  /**
   * Bind modal-related event listeners
   */
  bindModalEvents() {
    const variantsModal = document.getElementById('productVariantsModal');
    if (variantsModal) {
      variantsModal.addEventListener('close', () => {
        if (this._skipCommitOnNextVariantsClose) {
          this._skipCommitOnNextVariantsClose = false;
          if (this._queuedCartSync) {
            this._queuedCartSync = false;
            this.queueSyncSelectionStateFromCart();
          }
          return;
        }
        this.commitSelectionsToCartDrawer();
      });
    }

    // Handle product detail modal close events
    const detailModal = document.getElementById('productDetailModal');
    if (detailModal) {
      // Function to sync modal state back to variant cards
      const syncOnClose = () => {
        if (this.currentVariantData) {
          const modalQuantityInput = document.getElementById('modalQuantityInput');
          if (modalQuantityInput) {
            const quantity = parseInt(modalQuantityInput.value) || 0;
            this.syncQuantities(this.currentVariantData.id, quantity);
          }
        }
      };
      
      // Listen for modal close event (includes ESC key)
      detailModal.addEventListener('close', syncOnClose);
      
      // Listen for close button clicks - dispatch click event to back button
      const closeBtn = detailModal.querySelector('.product-detail-modal__close');
      const backBtn = document.getElementById('backToProducts');
      
      if (closeBtn && backBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          backBtn.click();
        });
      }
      
      // Listen for back button clicks
      if (backBtn) {
        backBtn.addEventListener('click', syncOnClose);
      }
      
      // Listen for backdrop clicks
      detailModal.addEventListener('click', (e) => {
        if (e.target === detailModal) {
          syncOnClose();
        }
      });
    }
  }

  /**
   * Bind subscription-related event listeners
   */
  bindSubscriptionEvents() {
    // Subscription events can be added here
  }

  /**
   * Bind quantity input events for both variant cards and modal
   */
  bindQuantityEvents() {
    // Handle quantity buttons in variant cards
    document.addEventListener('click', (e) => {
      const quantityBtn = e.target.closest('.quantity-btn');
      if (quantityBtn) {
        e.preventDefault();
        e.stopPropagation();
        
        const action = quantityBtn.dataset.action;
        const quantityContainer = quantityBtn.closest('.product-variant-card__quantity, .product-detail-modal__quantity');
        const input = quantityContainer.querySelector('.quantity-input');
        
        if (input) {
          this.updateQuantity(input, action);
        }
      }
    });

    // Handle direct input changes
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('quantity-input')) {
        const input = e.target;
        const oldValue = parseInt(input.dataset.previousValue) || 0;
        
        // Validate the input first
        this.validateQuantityInput(input);
        
        const newValue = parseInt(input.value) || 0;
        
        // Only proceed if value actually changed
        if (oldValue !== newValue) {
          // Store the new value for next comparison
          input.dataset.previousValue = newValue;
          
          // Get variant data for this input
          let variantId, variantData;
          
          // Check if this is a variant card input
          const variantCard = input.closest('.product-variant-card');
          if (variantCard) {
            variantId = input.dataset.variantId;
            variantData = this.getVariantDataFromCard(variantCard);
          }
          
          // Check if this is the modal input
          if (input.id === 'modalQuantityInput' && this.currentVariantData) {
            variantId = this.currentVariantData.id;
            variantData = this.currentVariantData;
          }
          
          if (variantId && variantData) {
            // Update the selected products array based on quantity change
            this.updateSelectedProductsByQuantity(variantData, oldValue, newValue);
            
            // Sync quantities between inputs
            this.syncQuantities(variantId, newValue);
            
            // Update cards display
            this.updateCards();
          }
        }
      }
    });
    
    // Initialize previous values for all quantity inputs
    document.querySelectorAll('.quantity-input').forEach(input => {
      input.dataset.previousValue = input.value || '0';
    });
  }

  /**
   * Bind Add to Cart button events
   */
  bindAddToCartEvents() {
    // Handle Add to Cart button clicks in variant cards
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.add-to-cart-btn');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        
        const variantId = button.dataset.variantId;
        
        if (variantId) {
          // This is a variant card button
          this.toggleToQuantityInput(variantId);
        } else if (button.id === 'modalAddToCartBtn') {
          // This is the modal button
          this.toggleModalToQuantityInput();
        }
      }
    });
  }

  /**
   * Toggle variant card from Add to Cart button to quantity input
   * @param {string} variantId - The variant ID
   */
  toggleToQuantityInput(variantId) {
    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
    if (!variantCard) return;
    
    const button = variantCard.querySelector('.add-to-cart-btn');
    const quantityDiv = variantCard.querySelector('.product-variant-card__quantity');
    const quantityInput = quantityDiv ? quantityDiv.querySelector('.quantity-input') : null;
    
    if (button && quantityDiv && quantityInput) {
      // Hide button and show quantity input
      button.style.display = 'none';
      quantityDiv.style.display = 'flex';
      
      // Set quantity to 1 and trigger the update
      quantityInput.value = '1';
      const variantData = this.getVariantDataFromCard(variantCard);
      
      if (variantData) {
        this.updateSelectedProductsByQuantity(variantData, 0, 1);
        this.syncQuantities(variantId, 1);
        this.updateCards();
      }
    }
  }

  /**
   * Toggle modal from Add to Cart button to quantity input
   */
  toggleModalToQuantityInput() {
    const button = document.getElementById('modalAddToCartBtn');
    const quantityDiv = document.querySelector('.product-detail-modal__quantity');
    const quantityInput = document.getElementById('modalQuantityInput');
    
    if (button && quantityDiv && quantityInput && this.currentVariantData) {
      // Hide button and show quantity input
      button.style.display = 'none';
      quantityDiv.style.display = 'flex';
      
      // Set quantity to 1 and trigger the update
      quantityInput.value = '1';
      const variantId = this.currentVariantData.id;
      
      this.updateSelectedProductsByQuantity(this.currentVariantData, 0, 1);
      this.syncQuantities(variantId, 1);
      this.updateCards();
    }
  }

  /**
   * Update quantity based on action
   * @param {HTMLElement} input - The quantity input element
   * @param {string} action - 'increase' or 'decrease'
   */
  updateQuantity(input, action) {
    let currentValue = parseInt(input.value) || 0;
    const min = parseInt(input.min) || 0;
    const max = parseInt(input.max) || 99;
    const oldValue = currentValue;
    
    if (action === 'increase' && currentValue < max) {
      currentValue++;
    } else if (action === 'decrease' && currentValue > min) {
      currentValue--;
    }
    
    input.value = currentValue;
    
    // Update previousValue to prevent infinite loops in input event
    input.dataset.previousValue = currentValue.toString();
    
    // Get variant data for this input
    let variantId, variantData;
    
    // Check if this is a variant card input
    const variantCard = input.closest('.product-variant-card');
    if (variantCard) {
      variantId = input.dataset.variantId;
      variantData = this.getVariantDataFromCard(variantCard);
    }
    
    // Check if this is the modal input
    if (input.id === 'modalQuantityInput' && this.currentVariantData) {
      variantId = this.currentVariantData.id;
      variantData = this.currentVariantData;
    }
    
    if (variantId && variantData) {
      // Update the selected products array based on quantity change
      this.updateSelectedProductsByQuantity(variantData, oldValue, currentValue);
      
      // Sync quantities between inputs
      this.syncQuantities(variantId, currentValue);
      
      // Also directly update visibility for the current input's card/modal
      if (variantCard) {
        const button = variantCard.querySelector('.add-to-cart-btn');
        const quantityDiv = variantCard.querySelector('.product-variant-card__quantity');
        if (button && quantityDiv) {
          if (currentValue > 0) {
            button.style.display = 'none';
            quantityDiv.style.display = 'flex';
          } else {
            button.style.display = 'flex';
            quantityDiv.style.display = 'none';
          }
        }
      }
      
      if (input.id === 'modalQuantityInput') {
        const modalButton = document.getElementById('modalAddToCartBtn');
        const modalQuantityDiv = document.querySelector('.product-detail-modal__quantity');
        if (modalButton && modalQuantityDiv) {
          if (currentValue > 0) {
            modalButton.style.display = 'none';
            modalQuantityDiv.style.display = 'flex';
          } else {
            modalButton.style.display = 'flex';
            modalQuantityDiv.style.display = 'none';
          }
        }
      }
      
      // Update cards display
      this.updateCards();
    }
  }

  /**
   * Validate quantity input value
   * @param {HTMLElement} input - The quantity input element
   */
  validateQuantityInput(input) {
    let value = parseInt(input.value);
    const min = parseInt(input.min) || 0;
    const max = parseInt(input.max) || 99;
    
    if (isNaN(value) || value < min) {
      value = min;
    } else if (value > max) {
      value = max;
    }
    
    input.value = value;
  }

  /**
   * Get variant data from a variant card element
   * @param {HTMLElement} variantCard - The variant card element
   * @returns {Object} - The variant data object
   */
  getVariantDataFromCard(variantCard) {
    const descriptionElement = variantCard.querySelector('.product-variant-card__description');
    return {
      id: variantCard.dataset.variantId,
      title: variantCard.dataset.variantTitle,
      image: variantCard.dataset.variantImage,
      description: descriptionElement ? descriptionElement.textContent : 'Product description not available.',
      available: variantCard.dataset.variantAvailable === 'true',
      inventory: parseInt(variantCard.dataset.variantInventory) || 0,
      price: parseInt(variantCard.dataset.variantPrice) || 0,
      comparePrice: parseInt(variantCard.dataset.variantComparePrice) || 0,
      badgeType: variantCard.dataset.badgeType,
      badgeText: variantCard.dataset.badgeText
    };
  }

  /**
   * Update selected products array based on quantity changes
   * @param {Object} variantData - The variant data
   * @param {number} oldQuantity - Previous quantity
   * @param {number} newQuantity - New quantity
   */
  updateSelectedProductsByQuantity(variantData, oldQuantity, newQuantity) {
    const variantId = variantData.id;
    
    // Remove all instances of this variant from the array
    this.selectedProducts = this.selectedProducts.filter(product => product.id !== variantId);
    
    // Add new instances based on the new quantity
    for (let i = 0; i < newQuantity; i++) {
      const product = {
        ...variantData,
        quantity: 1, // Each instance represents quantity 1
        addedAt: new Date().toISOString(),
        instanceIndex: i // Track which instance this is
      };
      this.selectedProducts.push(product);
    }
    
    // Dispatch events for product selection change
    document.dispatchEvent(new CustomEvent('productSelection:changed', {
      detail: { selectedProducts: this.selectedProducts }
    }));
    
    // Force button state update
    if (window.customAddToCartSyncManager) {
      setTimeout(() => {
        window.customAddToCartSyncManager.handleProductSelectionChange();
      }, 100);
    }

    // Keep cart icon bubble in sync immediately for modal/slider-originated edits.
    if (!this._isApplyingOptimisticFromCart) {
      this.syncCartBubbleFromSelection();
    }

    // Keep cart drawer visuals in sync on every modal/slider edit.
    if (!this._isApplyingOptimisticFromCart) {
      this.beginOptimisticHydrationDefer(1000);
      this.applyOptimisticCartDrawerFromSelection();
      this.scheduleSelectionToCartSync();
    }
  }

  /**
   * Sync quantities between variant card and modal
   * @param {string} variantId - The variant ID
   * @param {number} quantity - The new quantity value
   */
  syncQuantities(variantId, quantity) {
    // Update all matching variant cards (safer when duplicate card nodes exist).
    const variantCards = document.querySelectorAll(`.product-variant-card[data-variant-id="${variantId}"]`);
    variantCards.forEach((variantCard) => {
      const variantInput = variantCard.querySelector('.quantity-input');
      const variantQuantityDiv = variantCard.querySelector('.product-variant-card__quantity');
      const variantButton = variantCard.querySelector('.add-to-cart-btn');
      if (!variantInput) return;

      if (variantInput.value != quantity) {
        variantInput.value = quantity;
        // Update previousValue to prevent infinite loops
        variantInput.dataset.previousValue = quantity.toString();
      }

      // Always handle visibility based on quantity (not just when value changes)
      if (variantQuantityDiv && variantButton) {
        if (quantity > 0) {
          // Show quantity input, hide button
          variantButton.style.display = 'none';
          variantQuantityDiv.style.display = 'flex';
        } else {
          // Show button, hide quantity input
          variantButton.style.display = 'flex';
          variantQuantityDiv.style.display = 'none';
        }
      }
    });
    
    // Update modal quantity if it's for the same variant
    const modalInput = document.getElementById('modalQuantityInput');
    const modalButton = document.getElementById('modalAddToCartBtn');
    const modalQuantityDiv = document.querySelector('.product-detail-modal__quantity');
    
    if (modalInput && this.currentVariantData && this.currentVariantData.id === variantId) {
      if (modalInput.value != quantity) {
        modalInput.value = quantity;
        // Update previousValue to prevent infinite loops
        modalInput.dataset.previousValue = quantity.toString();
      }
      
      // Handle modal visibility based on quantity
      if (modalButton && modalQuantityDiv) {
        if (quantity > 0) {
          // Show quantity input, hide button
          modalButton.style.display = 'none';
          modalQuantityDiv.style.display = 'flex';
        } else {
          // Show button, hide quantity input
          modalButton.style.display = 'flex';
          modalQuantityDiv.style.display = 'none';
        }
      }
    }
  }

  /**
   * Open product selection modal for specific buy card
   * @param {number} cardNumber - The buy card number (1-4)
   */
  openProductSelection(cardNumber) {
    this.currentCardNumber = cardNumber;
    
    // Open the variants modal
    const modal = document.getElementById('productVariantsModal');
    if (modal) {
      document.body.style.overflow = 'hidden';
      modal.showModal();
    }
  }

  /**
   * Find the first available slot and open product selection
   */
  findAndOpenAvailableSlot() {
    const quantityOptions = document.querySelectorAll('.swiper-slide:not(.duplicate-slide) .quantity-option');
    let availableSlot = null;

    for (let i = 0; i < quantityOptions.length; i++) {
      const card = quantityOptions[i];
      const hasProduct = card.querySelector('.quantity-option__product-info') || card.querySelector('.quantity-option__product-image');
      if (!hasProduct) {
        availableSlot = i + 1;
        break;
      }
    }

    if (availableSlot) {
      this.openProductSelection(availableSlot);
    } else {
      // If no available slot, use the first slot (slot 1) to allow product replacement
      this.openProductSelection(1);
    }
  }

  /**
   * Handle variant selection
   * @param {HTMLElement} variantCard - The selected variant card element
   */
  selectVariant(variantCard) {
    const descriptionElement = variantCard.querySelector('.product-variant-card__description');
    const variantData = {
      id: variantCard.dataset.variantId,
      title: variantCard.dataset.variantTitle,
      image: variantCard.dataset.variantImage,
      description: descriptionElement ? descriptionElement.textContent : 'Product description not available.',
      available: variantCard.dataset.variantAvailable === 'true',
      inventory: parseInt(variantCard.dataset.variantInventory) || 0,
      price: parseInt(variantCard.dataset.variantPrice) || 0,
      comparePrice: parseInt(variantCard.dataset.variantComparePrice) || 0,
      badgeType: variantCard.dataset.badgeType,
      badgeText: variantCard.dataset.badgeText
    };

    // If the product detail modal is disabled, clicking the card should behave like clicking "Add to Cart".
    const detailModal = document.getElementById('productDetailModal');
    const isDetailModalEnabled = !(detailModal && detailModal.dataset && detailModal.dataset.csEnabled === 'false');
    if (!isDetailModalEnabled) {
      if (variantData.id) {
        // If quantity selector is already visible, tapping the card should increment quantity.
        const quantityDiv = variantCard.querySelector('.product-variant-card__quantity');
        const input = quantityDiv ? quantityDiv.querySelector('.quantity-input') : null;
        const isQuantityVisible = quantityDiv && window.getComputedStyle(quantityDiv).display !== 'none';

        if (isQuantityVisible && input) {
          this.updateQuantity(input, 'increase');
        } else {
          // First tap behaves like clicking "Add" (sets to 1 and reveals quantity UI)
          this.toggleToQuantityInput(variantData.id);
        }
      }
      return;
    }

    // Store the selected variant data
    this.currentVariantData = variantData;

    // Open product detail modal
    this.openProductDetailModal(variantData);
  }

  /**
   * Open product detail modal with variant data
   * @param {Object} variantData - The variant data object
   */
  openProductDetailModal(variantData) {
    const modal = document.getElementById('productDetailModal');
    // Respect theme/section setting: optionally disable opening the product detail modal entirely
    if (modal && modal.dataset && modal.dataset.csEnabled === 'false') {
      return;
    }
    const image = document.getElementById('productDetailImage');
    const title = document.getElementById('productDetailTitle');
    const description = document.querySelector('.product-detail-modal__description p');
    const modalQuantityInput = document.getElementById('modalQuantityInput');
    const modalButton = document.getElementById('modalAddToCartBtn');
    const modalQuantityDiv = document.querySelector('.product-detail-modal__quantity');
    
    // Populate modal with variant data
    if (image) {
      image.src = variantData.image;
      image.alt = variantData.title;
    }
    if (title) {
      title.textContent = variantData.title;
    }
    if (description && variantData.description) {
      description.textContent = variantData.description;
    }
    
    // Check variant card visibility state and sync to modal
    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantData.id}"]`);
    const variantQuantityDiv = variantCard ? variantCard.querySelector('.product-variant-card__quantity') : null;
    const variantButton = variantCard ? variantCard.querySelector('.add-to-cart-btn') : null;
    const variantInput = variantQuantityDiv ? variantQuantityDiv.querySelector('.quantity-input') : null;
    
    if (modalQuantityInput && modalButton && modalQuantityDiv) {
      // Check if variant card is showing quantity input or button
      const isQuantityVisible = variantQuantityDiv && window.getComputedStyle(variantQuantityDiv).display !== 'none';
      const quantity = variantInput ? variantInput.value : '0';
      
      // Sync quantity value
      modalQuantityInput.value = quantity;
      modalQuantityInput.dataset.previousValue = quantity;
      
      // Sync visibility state
      if (isQuantityVisible) {
        // Show quantity input, hide button
        modalButton.style.display = 'none';
        modalQuantityDiv.style.display = 'flex';
      } else {
        // Show button, hide quantity input
        modalButton.style.display = 'flex';
        modalQuantityDiv.style.display = 'none';
      }
    }
    
    // Close variants modal and open detail modal.
    // Do not commit to cart when this close is only a modal-to-modal transition.
    this.closeVariantsModal({ skipCommit: true });
    if (modal) {
      modal.showModal();
    }
  }



  /**
   * Update the buy cards based on quantity inputs from variant cards
   */
  updateCards() {
    this.maxProducts = this.getBaseSlotCount();
    const quantityOptions = document.querySelectorAll('.quantity-option');
    const variantCards = document.querySelectorAll('.product-variant-card');
    
    // Reset all original cards first
    quantityOptions.forEach((option, index) => {
      this.resetCard(option, index + 1);
    });

    // Remove any existing duplicate cards
    this.removeDuplicateCards();

    // Collect all individual quantity units from all variants
    const individualSlides = [];
    
    variantCards.forEach(variantCard => {
      const quantityInput = variantCard.querySelector('.quantity-input');
      const quantity = parseInt(quantityInput?.value) || 0;
      
      if (quantity > 0) {
        const variantData = this.getVariantDataFromCard(variantCard);
        if (variantData) {
          // Create individual slides for each quantity unit
          for (let i = 0; i < quantity; i++) {
            individualSlides.push({
              ...variantData,
              quantity: 1, // Each slide represents 1 unit
              variantId: variantData.id,
              slideIndex: i // Track which unit this is for the same variant
            });
          }
        }
      }
    });

    // Sort slides by variant ID for better organization
    individualSlides.sort((a, b) => {
      if (a.variantId === b.variantId) {
        return a.slideIndex - b.slideIndex; // Same variant, sort by slide index
      }
      return a.variantId.localeCompare(b.variantId); // Different variants, sort by ID
    });

    // Fill original cards (up to 4)
    individualSlides.forEach((slideData, index) => {
      if (index < this.maxProducts) {
        this.fillCard(quantityOptions[index], slideData, index + 1);
      }
    });

    // Create duplicate slides for overflow (each quantity unit gets its own slide)
    if (individualSlides.length > this.maxProducts) {
      this.createDuplicateSlides(individualSlides.slice(this.maxProducts));
    }

    // Update swiper after card changes
    this.updateSwiper();

    // Update savings badge based on selected products
    this.updateSavingsBadge();
    
    // Update pricing display based on selected products
   
    this.updatePricingDisplay();
    
  }

  /**
   * Update the savings badge based on selected products and their discount values
   */
  updateSavingsBadge() {
    // Find all savings badge instances (main, modal, sticky)
    const savingsBadges = [
      { badge: document.getElementById('savingsBadge'), text: document.getElementById('savingsText') },
      { badge: document.getElementById('savingsBadgeModal'), text: document.getElementById('savingsTextModal') },
      { badge: document.getElementById('savingsBadgeSticky'), text: document.getElementById('savingsTextSticky') }
    ];

    const includeCompareAt = savingsBadges.some(({ badge }) => badge?.dataset?.csIncludeCompareAt === 'true');

    if (includeCompareAt) {
      // Combined savings: (compare-at savings + bundle discount)
      let totalPrice = 0;
      let totalComparePrice = 0;
      let hasSavings = false;

      this.selectedProducts.forEach((product) => {
        const variantPrice = product.price || 0;
        const variantComparePrice = product.comparePrice || 0;

        totalPrice += variantPrice;

        if (variantComparePrice && variantComparePrice > variantPrice) {
          totalComparePrice += variantComparePrice;
          hasSavings = true;
        } else {
          totalComparePrice += variantPrice;
        }
      });

      const bundleDiscount = this.getBundleDiscount(this.selectedProducts.length);
      const discountInCents = Math.round(bundleDiscount * 100);

      let finalPrice = Math.max(0, totalPrice - discountInCents);
      if (document.body.classList.contains('template-flower-2')) {
        // Flower-2 template intentionally disables bundle discount in pricing display
        finalPrice = Math.max(0, totalPrice);
      }

      const savingsInCents = Math.max(0, totalComparePrice - finalPrice);
      const savingsDollars = savingsInCents / 100;

      savingsBadges.forEach(({ badge, text }) => {
        if (!badge || !text) return;
        if (savingsInCents > 0 && (hasSavings || discountInCents > 0)) {
          text.textContent = `YOU SAVE $${savingsDollars.toFixed(0)}`;
          badge.style.display = 'block';
        } else {
          badge.style.display = 'none';
        }
      });

      return;
    }

    // Find the maximum discount from all selected products
    let maxDiscount = 0;
    
    this.selectedProducts.forEach((product, index) => {
      // Get the corresponding quantity option card
      const quantityOption = document.querySelectorAll('.quantity-option')[index];
      if (quantityOption) {
        // Get the swiper slide that contains this quantity option
        const swiperSlide = quantityOption.closest('.swiper-slide');
        if (swiperSlide && swiperSlide.dataset.discount) {
          const discount = parseFloat(swiperSlide.dataset.discount) || 0;
          if (discount > maxDiscount) {
            maxDiscount = discount;
          }
        }
      }
    });

    // Update all savings badge displays
    savingsBadges.forEach(({ badge, text }) => {
      if (badge && text) {
        if (maxDiscount > 0) {
          text.textContent = `YOU SAVE EXTRA $${maxDiscount.toFixed(0)}`;
          badge.style.display = 'block';
        } else {
          badge.style.display = 'none';
        }
      }
    });
  }

  /**
   * Get bundle discount based on quantity of selected products
   */
  getBundleDiscount(quantity) {
    // Find the swiper slide that corresponds to this quantity
    const quantityOptions = document.querySelectorAll('.quantity-option');
    
    // Look for the quantity option that matches our selected quantity
    for (let i = 0; i < quantityOptions.length; i++) {
      const quantityOption = quantityOptions[i];
      const swiperSlide = quantityOption.closest('.swiper-slide');
      
      if (swiperSlide) {
        // Check if this slide represents the quantity we're looking for
        // The quantity is typically represented by the index + 1
        const slideQuantity = i + 1;
        
        if (slideQuantity === quantity && swiperSlide.dataset.discount) {
          return parseFloat(swiperSlide.dataset.discount) || 0;
        }
      }
    }
    
    return 0; // No discount found
  }

  /**
   * Update the pricing display based on selected products
   */
  updatePricingDisplay() {
    // Find all pricing instances (main, modal, sticky)
    const pricingInstances = [
      {
        price: document.querySelector('.product-selection__price'),
        comparePrice: document.querySelector('.product-selection__compare-price')
      },
      {
        price: document.querySelector('.modal-add-to-cart .product-selection__price'),
        comparePrice: document.querySelector('.modal-add-to-cart .product-selection__compare-price')
      },
      {
        price: document.querySelector('.sticky-add-to-cart .product-selection__price'),
        comparePrice: document.querySelector('.sticky-add-to-cart .product-selection__compare-price')
      }
    ];

    // If no products selected, show default price
    if (this.selectedProducts.length === 0) {
      // Get the default variant price from the page
      const defaultPrice = this.getDefaultPrice();
      const defaultComparePrice = this.getDefaultComparePrice();
      
      pricingInstances.forEach(({ price, comparePrice }) => {
        if (price) {
          price.textContent = defaultPrice;
        }
        
        if (comparePrice) {
          if (defaultComparePrice) {
            comparePrice.textContent = defaultComparePrice;
            comparePrice.style.display = 'inline';
          } else {
            comparePrice.style.display = 'none';
          }
        }
      });
      return;
    }

    // Calculate total price from selected products
    let totalPrice = 0;
    let totalComparePrice = 0;
    let hasComparePrice = false;

    this.selectedProducts.forEach(product => {
      // Use the price data from variant selection (stored in cents)
      const variantPrice = product.price || 0;
      const variantComparePrice = product.comparePrice || 0;
      
      totalPrice += variantPrice;
      
      if (variantComparePrice && variantComparePrice > variantPrice) {
        totalComparePrice += variantComparePrice;
        hasComparePrice = true;
      } else {
        totalComparePrice += variantPrice;
      }
    });

    // Apply bundle discount based on quantity
    const bundleDiscount = this.getBundleDiscount(this.selectedProducts.length);
    const discountInCents = bundleDiscount * 100; // Convert to cents
    let finalPrice = Math.max(0, totalPrice - discountInCents); // Ensure price doesn't go negative
     if (document.body.classList.contains('template-flower-2')) {
      finalPrice =  Math.max(0, totalPrice);
     }
    // Format and display the final price for all instances
    const formattedPrice = this.formatPrice(finalPrice);
    const formattedComparePrice = this.formatPrice(totalComparePrice);

    pricingInstances.forEach(({ price, comparePrice }) => {
      if (price) {
        price.textContent = formattedPrice;
      }
      
      if (comparePrice) {
        if (hasComparePrice && totalComparePrice > totalPrice) {
          // Keep compare price unchanged (no discount applied)
          comparePrice.textContent = formattedComparePrice;
          comparePrice.style.display = 'inline';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    });
  }

  /**
   * Get the default product price from the page
   */
  getDefaultPrice() {
    // Try to get from the pricing container data attribute
    const pricingContainer = document.querySelector('.product-selection__pricing[data-product-price]');
    if (pricingContainer && pricingContainer.dataset.productPrice) {
      return pricingContainer.dataset.productPrice;
    }
    
    // Fallback: try to parse from existing price element
    const priceElement = document.querySelector('.product-selection__price');
    if (priceElement && priceElement.textContent) {
      return priceElement.textContent.trim();
    }
    
    return '$0.00';
  }

  /**
   * Get the default product compare price from the page
   */
  getDefaultComparePrice() {
    // Try to get from the pricing container data attribute
    const pricingContainer = document.querySelector('.product-selection__pricing[data-product-compare-price]');
    if (pricingContainer && pricingContainer.dataset.productComparePrice) {
      return pricingContainer.dataset.productComparePrice;
    }
    
    // Fallback: try to parse from existing compare price element
    const comparePriceElement = document.querySelector('.product-selection__compare-price');
    if (comparePriceElement && comparePriceElement.textContent && comparePriceElement.style.display !== 'none') {
      return comparePriceElement.textContent.trim();
    }
    
    return null;
  }

  /**
   * Format price for display
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents) {
    if (!priceInCents || priceInCents === 0) return '$0.00';
    
    const dollars = priceInCents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  /**
   * Reset a card to its default state
   * @param {HTMLElement} cardElement - The card element
   * @param {number} cardNumber - The card number
   */
  resetCard(cardElement, cardNumber) {
    const plusBtn = cardElement.querySelector('.quantity-option__plus-btn');
    const info = cardElement.querySelector('.quantity-option__info');
    
    // Remove any existing product image
    const existingImage = cardElement.querySelector('.quantity-option__product-image');
    if (existingImage) {
      existingImage.remove();
    }

    // Reset plus button
    if (plusBtn) {
      plusBtn.style.display = 'flex';
      plusBtn.style.opacity = '1';
    }

    // Reset card styling
    cardElement.classList.remove('quantity-option--filled');
    
    // Reset info content and make it visible
    if (info) {
      info.style.display = 'block'; // Make sure info is visible
      const savings = info.querySelector('.quantity-option__savings');
      const label = info.querySelector('.quantity-option__label');
      
      if (savings) {
        savings.style.display = savings.textContent.trim() ? 'block' : 'none';
      }
      if (label) {
        label.textContent = cardElement.getAttribute('data-quantity-label') || `Buy ${cardNumber}`;
        label.style.display = 'block';
      }
    }
  }

  /**
   * Fill a card with product data
   * @param {HTMLElement} cardElement - The card element
   * @param {Object} product - The product data
   * @param {number} cardNumber - The card number
   */
  fillCard(cardElement, product, cardNumber) {
    const plusBtn = cardElement.querySelector('.quantity-option__plus-btn');
    const info = cardElement.querySelector('.quantity-option__info');
    
    // Hide plus button and info when card is filled
    if (plusBtn) {
      plusBtn.style.display = 'none';
    }
    if (info) {
      info.style.display = 'none';
    }
    
    // Add filled class
    cardElement.classList.add('quantity-option--filled');

    // Create or update product image
    let productImage = cardElement.querySelector('.quantity-option__product-image');
    if (!productImage) {
      productImage = document.createElement('div');
      productImage.className = 'quantity-option__product-image';
      cardElement.querySelector('.quantity-option__content').appendChild(productImage);
    }

    // Generate stock label based on badge metafields
    let stockLabel = '';
    if (product.badgeType === 'Stock' && product.inventory !== undefined && product.inventory <= 10) {
      stockLabel = `${product.inventory} LEFT`;
    } else if (product.badgeType === 'Custom Text' && product.badgeText) {
      stockLabel = product.badgeText;
    }
    
    // Show quantity in the card title
    const quantityText = product.quantity > 1 ? ` (x${product.quantity})` : '';
    
    productImage.innerHTML = `
      <img src="${product.image}" alt="${product.title}" />
      <div class="quantity-option__product-title">${product.title}${quantityText}</div>
      <div class="quantity-option__product-overlay">
        <div class="quantity-option__product-actions">
        ${stockLabel ? `<div class="quantity-option__stock-label">${stockLabel}</div>` : ''}
          <button class="quantity-option__clear-btn" data-variant-id="${product.variantId}" title="Clear product">
            <svg class="icon icon-close" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Add event listener to existing clear button for individual unit removal
    const clearBtn = productImage.querySelector('.quantity-option__clear-btn');
    if (clearBtn) {
      clearBtn.onclick = (e) => {
        e.stopPropagation();
        this.removeIndividualUnit(product.variantId);
      };
    }
  }

  /**
   * Close variants modal
   */
  closeVariantsModal({ skipCommit = false } = {}) {
    const modal = document.getElementById('productVariantsModal');
    if (skipCommit) this._skipCommitOnNextVariantsClose = true;
    if (modal) {
      document.body.style.overflow = '';
      modal.close();
    }
  }

  getSelectedVariantQuantities() {
    const quantities = new Map();
    this.selectedProducts.forEach((product) => {
      const id = String(product?.id || '').trim();
      if (!id) return;
      quantities.set(id, (quantities.get(id) || 0) + 1);
    });
    return quantities;
  }

  getManagedVariantIds() {
    return new Set(
      Array.from(document.querySelectorAll('.product-variant-card[data-variant-id]'))
        .map((card) => String(card.dataset.variantId || '').trim())
        .filter(Boolean)
    );
  }

  isSubscriptionLineItemFromProperties(properties) {
    if (!properties) return false;
    const keys = Object.keys(properties);
    for (const rawKey of keys) {
      const key = String(rawKey || '').toLowerCase();
      if (
        key.includes('shipping_interval_') ||
        key.includes('order_interval_') ||
        key.includes('charge_interval_') ||
        key.includes('recharge') ||
        key.includes('subscription') ||
        key.includes('selling_plan')
      ) {
        return true;
      }
    }
    return false;
  }

  getCurrentPurchaseSelection() {
    const subscribeCheckboxes = Array.from(
      document.querySelectorAll('.subscription__checkbox[data-sync-group="subscription"][data-type="subscribe"]')
    );
    const subscribeChecked = subscribeCheckboxes.some((checkbox) => checkbox.checked);
    const selectEls = Array.from(document.querySelectorAll('.subscription__select'));

    let sellingPlanId = '';
    if (subscribeChecked) {
      for (const select of selectEls) {
        if (!select) continue;
        const selected = String(select.value || '').trim();
        if (selected) {
          sellingPlanId = String(parseInt(selected, 10) || '').trim();
          if (sellingPlanId) break;
        }
      }
      if (!sellingPlanId && selectEls[0]?.options?.length) {
        sellingPlanId = String(parseInt(selectEls[0].options[0].value, 10) || '').trim();
      }
    }

    const purchaseType = subscribeChecked ? 'subscription' : 'one-time';
    const signatureSellingPlanId = purchaseType === 'subscription' ? sellingPlanId : '';
    const signatureSuffix = `${signatureSellingPlanId}::${purchaseType}`;

    return {
      purchaseType,
      sellingPlanId: signatureSellingPlanId,
      signatureSuffix,
    };
  }

  getSubscriptionLabelForCurrentSelection() {
    const { purchaseType, sellingPlanId } = this.getCurrentPurchaseSelection();
    if (purchaseType !== 'subscription') return '';
    if (!sellingPlanId) return 'Subscription';

    const allSelects = Array.from(document.querySelectorAll('.subscription__select'));
    for (const select of allSelects) {
      if (!select) continue;
      const match = Array.from(select.options || []).find((option) => String(option.value || '').trim() === String(sellingPlanId));
      if (match && match.textContent) return match.textContent.trim();
    }

    return 'Subscription';
  }

  syncCartBubbleFromSelection() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.setCartBubbleCount !== 'function') return;

    // When variant-card ATC grid is active, this manager is not the source of truth
    // for selected quantities. Use drawer rows directly to avoid stale overwrites.
    const hasVariantGridAtc = Boolean(
      document.querySelector('variant-selects[data-cs-variant-grid-atc="true"] .cs-variant-grid-atc[data-variant-id]')
    );
    if (hasVariantGridAtc) {
      const drawerRows = Array.from(cartDrawer.querySelectorAll('.cs-cart-item[data-cs-variant-id]'));
      if (drawerRows.length) {
        const totalQty = drawerRows.reduce((sum, row) => {
          const qty = Math.max(0, parseInt(row.querySelector('.cs-qty__count')?.textContent || '0', 10) || 0);
          return sum + qty;
        }, 0);
        cartDrawer.setCartBubbleCount(totalQty);
      }
      return;
    }

    const managedVariantIds = this.getManagedVariantIds();
    const managedQty = this.selectedProducts.length;
    const drawerRows = Array.from(cartDrawer.querySelectorAll('.cs-cart-item[data-cs-variant-id]'));

    // Keep non-managed cart items in bubble count, only replace managed portion.
    if (drawerRows.length) {
      let nonManagedQty = 0;
      drawerRows.forEach((row) => {
        const variantId = String(row.dataset.csVariantId || '').trim();
        if (!variantId || managedVariantIds.has(variantId)) return;
        const qty = Math.max(0, parseInt(row.querySelector('.cs-qty__count')?.textContent || '0', 10) || 0);
        nonManagedQty += qty;
      });
      cartDrawer.setCartBubbleCount(nonManagedQty + managedQty);
      return;
    }

    cartDrawer.setCartBubbleCount(managedQty);
  }

  getOptimisticCartListContainer(cartDrawer) {
    const itemsContainer = cartDrawer.querySelector('#CartDrawer-CartItems .cs-cart-drawer__items');
    return (
      itemsContainer ||
      cartDrawer.querySelector('cart-drawer-items .cart-items') ||
      cartDrawer.querySelector('cart-drawer-items .js-contents > div') ||
      cartDrawer.querySelector('cart-drawer-items .js-contents') ||
      cartDrawer.querySelector('cart-drawer-items form')
    );
  }

  createOptimisticCartRow(variantData, qty, templateRow = null, options = {}) {
    const purchaseType = options.purchaseType === 'subscription' ? 'subscription' : 'one-time';
    const sellingPlanId = purchaseType === 'subscription' ? String(options.sellingPlanId || '').trim() : '';
    const subscriptionLabel = purchaseType === 'subscription' ? String(options.subscriptionLabel || 'Subscription').trim() : '';
    const variantId = String(variantData?.id || '').trim();
    if (!variantId) return null;
    const safeTitle = String(variantData?.title || 'Product');
    const variantUrl = `${window.location.pathname}?variant=${encodeURIComponent(variantId)}`;
    const imageUrl = String(variantData?.image || '');
    const unitPrice = parseInt(variantData?.price || 0, 10) || 0;
    const unitCompareAtPrice = parseInt(variantData?.comparePrice || 0, 10) || 0;
    const totalPrice = unitPrice * Math.max(0, qty || 0);
    const totalCompareAtPrice = unitCompareAtPrice * Math.max(0, qty || 0);

    const row = templateRow ? templateRow.cloneNode(true) : document.createElement('div');
    if (!templateRow) {
      row.className = 'cart-item cs-cart-item';
      row.innerHTML = `
        <div class="cs-cart-item__loading-overlay" aria-hidden="true"></div>
        <div class="cart-item__media cs-cart-item__media">
          <a href="${variantUrl}" class="cart-item__link" tabindex="-1" aria-hidden="true"></a>
          ${imageUrl ? `<img class="cart-item__image cs-cart-item__image" src="${imageUrl}" alt="${safeTitle}" loading="lazy" width="132" height="132">` : ''}
        </div>
        <div class="cs-cart-item__content">
          <div class="cs-cart-item__top">
            <a href="${variantUrl}" class="cart-item__name cs-cart-item__name">${safeTitle}</a>
            <button type="button" class="cs-cart-item__remove" data-cs-remove-group aria-label="Remove ${safeTitle}">
              <svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.3537 2.4075C13.898 1.93922 13.2616 1.67027 12.6082 1.67027H2.39209C1.7387 1.67027 1.10231 1.93887 0.646641 2.4075C0.20904 2.8575 -0.0202142 3.43969 0.00140011 4.04895C0.0176994 4.4775 0.343686 4.81992 0.762154 4.86914L1.25893 15.6551C1.32023 16.9699 2.36976 18 3.64855 18H11.3518C12.6305 18 13.6801 16.9699 13.741 15.6544L14.2378 4.86879C14.6563 4.81957 14.9823 4.47715 14.9986 4.04754C15.0205 3.43969 14.7913 2.85715 14.3537 2.4075Z" fill="#333333"/>
              </svg>
            </button>
          </div>
          <div class="cs-cart-item__variant">${safeTitle}</div>
          <div class="cs-cart-item__prices">
            <div class="cs-cart-item__price">${this.formatPrice(totalPrice)}</div>
          </div>
          <div class="cs-cart-item__qty cart-item__quantity">
            <div class="quantity cs-qty">
              <button class="quantity__button${qty <= 1 ? ' disabled' : ''}" type="button" data-cs-qty-action="minus">
                <span class="cs-qty__icon cs-qty__icon--active">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.2025" y="0.2025" width="17.595" height="17.595" rx="3.8475" stroke="black" stroke-width="0.405"/>
                    <path d="M12.7308 8.28012V10.0801H5.3999V8.28012H12.7308Z" fill="#1E1E1E"/>
                  </svg>
                </span>
                <span class="cs-qty__icon cs-qty__icon--inactive">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.2025" y="0.2025" width="17.595" height="17.595" rx="3.8475" stroke="black" stroke-width="0.405"/>
                    <path d="M12.7308 8.28012V10.0801H5.3999V8.28012H12.7308Z" fill="#1E1E1E" fill-opacity="0.25"/>
                  </svg>
                </span>
              </button>
              <span class="cs-qty__count">${qty}</span>
              <button class="quantity__button" type="button" data-cs-qty-action="plus">
                <span class="cs-qty__icon cs-qty__icon--active">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.18" y="0.18" width="17.64" height="17.64" rx="3.42" stroke="black" stroke-width="0.36"/>
                    <path d="M8.0429 13.0499V4.94989H9.41745V13.0499H8.0429ZM4.68018 9.68716V8.31262H12.7802V9.68716H4.68018Z" fill="black"/>
                  </svg>
                </span>
                <span class="cs-qty__icon cs-qty__icon--inactive">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.18" y="0.18" width="17.64" height="17.64" rx="3.42" stroke="black" stroke-width="0.36"/>
                    <path d="M8.0429 13.0499V4.94989H9.41745V13.0499H8.0429ZM4.68018 9.68716V8.31262H12.7802V9.68716H4.68018Z" fill="black" fill-opacity="0.25"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      `;
    }

    row.classList.add('cs-cart-item--optimistic');
    row.dataset.csGroupKeys = `optimistic-${variantId}`;
    row.dataset.csGroupQtys = String(qty);
    row.dataset.csVariantId = variantId;
    row.dataset.csSellingPlanId = sellingPlanId;
    row.dataset.csPurchaseType = purchaseType;

    const mediaLink = row.querySelector('.cart-item__link');
    if (mediaLink) mediaLink.setAttribute('href', variantUrl);
    const titleLink = row.querySelector('.cs-cart-item__name');
    if (titleLink) {
      titleLink.textContent = safeTitle;
      titleLink.setAttribute('href', variantUrl);
    }
    const variantEl = row.querySelector('.cs-cart-item__variant');
    if (variantEl) {
      variantEl.textContent = safeTitle;
      variantEl.style.display = '';
    }

    const imageEl = row.querySelector('.cs-cart-item__image');
    if (imageEl && imageUrl) {
      imageEl.setAttribute('src', imageUrl);
      imageEl.setAttribute('alt', safeTitle);
    }

    const compareEl = row.querySelector('.cs-cart-item__compare');
    const pricesEl = row.querySelector('.cs-cart-item__prices');
    if (totalCompareAtPrice > totalPrice && totalCompareAtPrice > 0) {
      if (compareEl) {
        compareEl.textContent = this.formatPrice(totalCompareAtPrice);
      } else if (pricesEl) {
        const createdCompare = document.createElement('s');
        createdCompare.className = 'cs-cart-item__compare';
        createdCompare.textContent = this.formatPrice(totalCompareAtPrice);
        pricesEl.appendChild(createdCompare);
      }
    } else if (compareEl) {
      compareEl.remove();
    }
    const subEl = row.querySelector('.cs-cart-item__subscription');
    if (purchaseType === 'subscription') {
      if (subEl) {
        subEl.textContent = subscriptionLabel || 'Subscription';
      } else {
        const pricesBlock = row.querySelector('.cs-cart-item__prices');
        if (pricesBlock?.parentElement) {
          const createdSub = document.createElement('div');
          createdSub.className = 'cs-cart-item__subscription';
          createdSub.textContent = subscriptionLabel || 'Subscription';
          pricesBlock.insertAdjacentElement('afterend', createdSub);
        }
      }
    } else if (subEl) {
      subEl.remove();
    }

    const priceEl = row.querySelector('.cs-cart-item__price');
    if (priceEl) priceEl.textContent = this.formatPrice(totalPrice);
    const countEl = row.querySelector('.cs-qty__count');
    if (countEl) countEl.textContent = String(qty);
    const minusBtn = row.querySelector('[data-cs-qty-action="minus"]');
    if (minusBtn) minusBtn.classList.toggle('disabled', qty <= 1);

    return row;
  }

  applyOptimisticCartDrawerFromSelection() {
    if (this.isVariantGridAtcActive()) return;
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    const qtyByVariant = this.getSelectedVariantQuantities();
    const managedVariantIds = this.getManagedVariantIds();
    const selectedPurchase = this.getCurrentPurchaseSelection();
    const selectedSubscriptionLabel = this.getSubscriptionLabelForCurrentSelection();
    const templateRow = cartDrawer.querySelector('.cs-cart-item[data-cs-variant-id]');
    const listContainer = this.getOptimisticCartListContainer(cartDrawer);
    const shouldOptimisticallyRenderNewRows = Boolean(templateRow);

    if (listContainer) {
      qtyByVariant.forEach((qty, variantId) => {
        if (qty <= 0) return;
        if (!managedVariantIds.has(String(variantId))) return;
        const existing = cartDrawer.querySelector(
          `.cs-cart-item[data-cs-variant-id="${variantId}"][data-cs-selling-plan-id="${selectedPurchase.sellingPlanId}"][data-cs-purchase-type="${selectedPurchase.purchaseType}"]`
        );
        if (existing) return;
        if (!shouldOptimisticallyRenderNewRows) {
          // Empty-cart first item must be rendered by server sections to keep exact native layout.
          return;
        }
        const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
        const variantData = variantCard ? this.getVariantDataFromCard(variantCard) : { id: variantId, title: 'Product', price: 0 };
        const optimisticRow = this.createOptimisticCartRow(variantData, qty, templateRow, {
          purchaseType: selectedPurchase.purchaseType,
          sellingPlanId: selectedPurchase.sellingPlanId,
          subscriptionLabel: selectedSubscriptionLabel,
        });
        if (!optimisticRow) return;
        const firstCartItem = listContainer.querySelector('.cs-cart-item');
        if (firstCartItem) listContainer.insertBefore(optimisticRow, firstCartItem);
        else listContainer.prepend(optimisticRow);
      });
    }

    const allRows = Array.from(cartDrawer.querySelectorAll('.cs-cart-item[data-cs-variant-id]'));
    if (!allRows.length) {
      this.syncCartBubbleFromSelection();
      return;
    }

    // If both optimistic and real row exist for same managed line, keep the real row.
    const rowsByLine = new Map();
    allRows.forEach((row) => {
      const variantId = String(row.dataset.csVariantId || '').trim();
      if (!variantId || !managedVariantIds.has(variantId)) return;
      const lineKey = `${variantId}::${String(row.dataset.csSellingPlanId || '').trim()}::${String(row.dataset.csPurchaseType || '').trim()}`;
      const group = rowsByLine.get(lineKey) || [];
      group.push(row);
      rowsByLine.set(lineKey, group);
    });
    rowsByLine.forEach((group) => {
      if (group.length <= 1) return;
      const realRow = group.find((row) => !row.classList.contains('cs-cart-item--optimistic'));
      const keep = realRow || group[0];
      group.forEach((row) => {
        if (row !== keep) row.remove();
      });
    });

    const items = Array.from(cartDrawer.querySelectorAll('.cs-cart-item[data-cs-variant-id]'));
    if (!items.length) return;

    const seen = new Set();
    items.forEach((item) => {
      const variantId = String(item.dataset.csVariantId || '').trim();
      if (!variantId) return;
      if (!managedVariantIds.has(variantId)) return;
      const qty = qtyByVariant.get(variantId) || 0;
      const lineKey = `${variantId}::${String(item.dataset.csSellingPlanId || '').trim()}::${String(item.dataset.csPurchaseType || '').trim()}`;
      const isDuplicate = seen.has(lineKey);
      seen.add(lineKey);
      const isDesiredPurchaseLine = lineKey === `${variantId}::${selectedPurchase.signatureSuffix}`;

      if (qty <= 0 || isDuplicate || !isDesiredPurchaseLine) {
        item.remove();
        return;
      }

      const countEl = item.querySelector('.cs-qty__count');
      if (countEl) countEl.textContent = String(qty);
      // Keep native grouped line metadata intact on server-rendered rows.
      // cart-drawer.js expects csGroupKeys/csGroupQtys arrays to stay aligned.
      if (item.classList.contains('cs-cart-item--optimistic') && item.dataset.csGroupQtys) {
        item.dataset.csGroupQtys = String(qty);
      }
      const priceEl = item.querySelector('.cs-cart-item__price');
      // Keep server prices for real cart rows (they include automatic discounts).
      // Only recompute for optimistic rows that do not have server totals yet.
      if (priceEl && item.classList.contains('cs-cart-item--optimistic')) {
        const card = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
        const priceCents = parseInt(card?.dataset?.variantPrice || '0', 10) || 0;
        const comparePriceCents = parseInt(card?.dataset?.variantComparePrice || '0', 10) || 0;
        if (priceCents) priceEl.textContent = this.formatPrice(priceCents * qty);
        const compareEl = item.querySelector('.cs-cart-item__compare');
        const compareTotal = comparePriceCents * qty;
        const finalTotal = priceCents * qty;
        if (compareTotal > finalTotal && compareTotal > 0) {
          if (compareEl) {
            compareEl.textContent = this.formatPrice(compareTotal);
          } else {
            const pricesEl = item.querySelector('.cs-cart-item__prices');
            if (pricesEl) {
              const createdCompare = document.createElement('s');
              createdCompare.className = 'cs-cart-item__compare';
              createdCompare.textContent = this.formatPrice(compareTotal);
              pricesEl.appendChild(createdCompare);
            }
          }
        } else if (compareEl) {
          compareEl.remove();
        }
      }
      const minusBtn = item.querySelector('[data-cs-qty-action="minus"]');
      if (minusBtn) minusBtn.classList.toggle('disabled', qty <= 1);
      const minusBtnOptimistic = item.querySelector('[data-psm-qty-action="minus"]');
      if (minusBtnOptimistic) minusBtnOptimistic.classList.toggle('disabled', qty <= 1);
    });

    if (typeof cartDrawer.applyOptimisticEmptyStateIfNeeded === 'function') {
      cartDrawer.applyOptimisticEmptyStateIfNeeded();
    }
    if (typeof cartDrawer.syncEmptyStateClass === 'function') {
      cartDrawer.syncEmptyStateClass();
    }
    this.syncCartBubbleFromSelection();
  }

  async commitSelectionsToCartDrawer() {
    if (this.isVariantGridAtcActive()) return;
    if (this._isCommittingSelections) return;

    this._isCommittingSelections = true;
    try {
      this.beginOptimisticHydrationDefer(1000);
      this.applyOptimisticCartDrawerFromSelection();
      // Persist in background; UI sync must remain local-first and non-blocking.
      this._selectionSyncVersion += 1;
      this.syncSelectionStateToCart(this._selectionSyncVersion).catch(() => {});
    } catch (e) {
      // best effort: leave UI untouched if cart commit fails
    } finally {
      this._isCommittingSelections = false;
    }
  }

  /**
   * Close product detail modal
   */
  closeProductDetailModal() {
    const modal = document.getElementById('productDetailModal');
    if (modal) {
      document.body.style.overflow = '';
      modal.close();
    }
  }

  /**
   * Get selected products
   * @returns {Array} Array of selected products
   */
  getSelectedProducts() {
    return this.selectedProducts;
  }

  /**
   * Clear all selected products
   */
  clearSelectedProducts() {
    this.selectedProducts = [];
    this.updateCards();
    
    // Dispatch custom event for products cleared
    document.dispatchEvent(new CustomEvent('products:updated', {
      detail: { selectedProducts: this.selectedProducts }
    }));
    document.dispatchEvent(new CustomEvent('productSelection:changed', {
      detail: { selectedProducts: this.selectedProducts }
    }));
  }

  /**
   * Remove a specific product by index
   * @param {number} index - The index of the product to remove
   */
  removeProduct(index) {
    if (index >= 0 && index < this.selectedProducts.length) {
      const removedProduct = this.selectedProducts.splice(index, 1)[0];
      
      // Remove duplicate cards before updating
      this.removeDuplicateCards();
      
      this.updateCards();
      
      // Dispatch custom event for product removal
      document.dispatchEvent(new CustomEvent('product:removed', {
        detail: { product: removedProduct, selectedProducts: this.selectedProducts }
      }));
      document.dispatchEvent(new CustomEvent('productSelection:changed', {
        detail: { selectedProducts: this.selectedProducts }
      }));

      
      // Force button state update
      if (window.customAddToCartSyncManager) {
        setTimeout(() => {
          window.customAddToCartSyncManager.handleProductSelectionChange();
        }, 100);
      }
    }
  }

  /**
   * Clear a specific product by variant ID (reset quantity to 0)
   * @param {string} variantId - The variant ID to clear
   */
  clearProductByVariantId(variantId) {
    // Remove duplicate cards before clearing
    this.removeDuplicateCards();
    
    // Find the variant card with this variant ID
    const variantCards = document.querySelectorAll('.product-variant-card');
    variantCards.forEach(card => {
      const cardVariantId = card.dataset.variantId;
      if (cardVariantId === variantId) {
        const quantityInput = card.querySelector('.quantity-input');
        if (quantityInput) {
          // Set quantity to 0, which will trigger the updateQuantity logic
          quantityInput.value = '0';
          // Trigger change event to update the system
          quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
    
    // Also check modal input if it exists
    const modalQuantityInput = document.getElementById('modalQuantityInput');
    const modalVariantId = modalQuantityInput?.dataset.variantId;
    if (modalQuantityInput && modalVariantId === variantId) {
      modalQuantityInput.value = '0';
      modalQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /**
   * Create duplicate slides for overflow individual quantity units
   * @param {Array} overflowSlides - Individual quantity units that exceed the 4 card limit
   */
  createDuplicateSlides(overflowSlides) {
    const swiperWrapper = document.querySelector('.product-selection-swiper .swiper-wrapper');
    if (!swiperWrapper) return;

    overflowSlides.forEach((slideData, index) => {
      const duplicateSlide = this.createDuplicateSlide(slideData, this.maxProducts + index + 1);
      swiperWrapper.appendChild(duplicateSlide);
    });
  }

  /**
   * Create a duplicate swiper slide by cloning the original slide structure
   * @param {Object} slideData - Individual slide data (represents 1 quantity unit)
   * @param {number} cardNumber - Card number for labeling
   * @returns {HTMLElement} - The created slide element
   */
  createDuplicateSlide(slideData, cardNumber) {
    // Find the first original slide to use as template
    const originalSlide = document.querySelector('.product-selection-swiper .swiper-slide:not(.duplicate-slide)');
    if (!originalSlide) return null;

    // Clone the original slide structure
    const slide = originalSlide.cloneNode(true);
    slide.classList.add('duplicate-slide');
    slide.dataset.discount = '0'; // Default discount for duplicates

    // Get the quantity option element
    const quantityOption = slide.querySelector('.quantity-option');
    if (quantityOption) {
      quantityOption.classList.add('quantity-option--filled');
      quantityOption.dataset.quantity = cardNumber;

      // Fill the slide with product data (same as fillCard method)
      this.fillSlide(quantityOption, slideData, cardNumber);
    }

    return slide;
  }

  /**
   * Remove all duplicate slides from the swiper
   */
  removeDuplicateCards() {
    const duplicateSlides = document.querySelectorAll('.duplicate-slide');
    duplicateSlides.forEach(slide => slide.remove());
  }

  /**
   * Fill a slide with product data (similar to fillCard but for individual slides)
   * @param {HTMLElement} quantityOption - The quantity option element
   * @param {Object} slideData - The slide data
   * @param {number} cardNumber - Card number
   */
  fillSlide(quantityOption, slideData, cardNumber) {
    const content = quantityOption.querySelector('.quantity-option__content');
    
    // Hide plus button and info when slide is filled
    const plusBtn = quantityOption.querySelector('.quantity-option__plus-btn');
    const info = quantityOption.querySelector('.quantity-option__info');
    if (plusBtn) {
      plusBtn.style.display = 'none';
    }
    if (info) {
      info.style.display = 'none';
    }

    // Create or update product image
    let productImage = quantityOption.querySelector('.quantity-option__product-image');
    if (!productImage) {
      productImage = document.createElement('div');
      productImage.className = 'quantity-option__product-image';
      content.appendChild(productImage);
    }

    // Stock label
    let stockLabel = '';
    if (slideData.badgeType === 'Stock' && slideData.inventory !== undefined && slideData.inventory <= 10) {
      stockLabel = `${slideData.inventory} LEFT`;
    } else if (slideData.badgeType === 'Custom Text' && slideData.badgeText) {
      stockLabel = slideData.badgeText;
    }

    // Update product image content with existing structure
    productImage.innerHTML = `
      <img src="${slideData.image}" alt="${slideData.title}" />
      <div class="quantity-option__product-title">${slideData.title}</div>
      <div class="quantity-option__product-overlay">
        <div class="quantity-option__product-actions">
        ${stockLabel ? `<div class="quantity-option__stock-label">${stockLabel}</div>` : ''}
          <button class="quantity-option__clear-btn" data-variant-id="${slideData.variantId}" title="Clear product">
            <svg class="icon icon-close" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Add event listener to existing clear button for individual unit removal
    const clearBtn = productImage.querySelector('.quantity-option__clear-btn');
    if (clearBtn) {
      clearBtn.onclick = (e) => {
        e.stopPropagation();
        this.removeIndividualUnit(slideData.variantId);
      };
    }
  }



  /**
   * Remove one individual unit of a variant and recalculate
   * @param {string} variantId - The variant ID to remove one unit from
   */
  removeIndividualUnit(variantId) {
    // Find the variant card
    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
    if (!variantCard) return;
    
    const quantityInput = variantCard.querySelector('.quantity-input');
    if (!quantityInput) return;
    
    const oldValue = parseInt(quantityInput.value) || 0;
    
    // Set quantity to 0
    quantityInput.value = '0';
    quantityInput.dataset.previousValue = '0';
    
    // Also update modal input if it's for the same variant
    const modalInput = document.getElementById('modalQuantityInput');
    if (modalInput && this.currentVariantData && this.currentVariantData.id === variantId) {
      modalInput.value = '0';
      modalInput.dataset.previousValue = '0';
    }
    
    // Get variant data and force update
    const variantData = this.getVariantDataFromCard(variantCard);
    if (variantData) {
      // Update selected products array
      this.updateSelectedProductsByQuantity(variantData, oldValue, 0);
      
      // Force sync quantities to handle visibility
      this.syncQuantities(variantId, 0);
      
      // Update cards display
      this.updateCards();
    }
  }

  /**
   * Update swiper after adding/removing slides
   */
  updateSwiper() {
    // Get the swiper instance
    const swiperElement = document.querySelector('.product-selection-swiper');
    if (swiperElement && swiperElement.swiper) {
      // Update swiper to recognize new slides
      swiperElement.swiper.update();
    }
  }


}

// Initialize the product selection manager when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Wait a bit to ensure all other scripts are loaded
  setTimeout(() => {
    window.productSelectionManager = new ProductSelectionManager();
  }, 100);
});